import { z } from 'zod';
/**
 * Schema for group/relationship type detection response from OpenAI
 */
export const GroupDetectionResponseSchema = z.object({
    groupMatches: z.array(z.object({
        groupType: z
            .enum(['OWNER', 'TENANT', 'VENDOR'])
            .nullable()
            .describe('Standard relationship type if detected'),
        customGroup: z
            .string()
            .nullable()
            .describe('Custom group string (only if groupType is null): "board member", "staff", etc.'),
        confidence: z
            .number()
            .min(0)
            .max(1)
            .describe('Confidence score from 0.7 to 1.0'),
        matchDetails: z
            .string()
            .describe('Explanation of why this group/relationship was detected'),
    })),
});
/**
 * Prompt template for detecting relationship types and custom groups
 * from conversation content (voice transcripts, SMS messages, etc.)
 */
export const groupDetectionPrompt = {
    name: 'group-detection',
    version: '1.0.0',
    systemPrompt: 'You are a relationship type detection assistant for property management conversations.',
    userPrompt: `Analyze the following message and identify any relationship types or groups the speaker identifies as.

Message content:
{{content}}

Detection Rules:
1. Look for explicit statements from the customer like "I'm a tenant", "I'm the owner", "I work here as..."
2. Look for implicit indicators from the customer like "my unit", "my property", "I rent here"
3. Standard relationship types to detect: OWNER, TENANT, VENDOR
4. Custom groups to detect: staff, board member, HOA president, contractor, maintenance, prospect, etc.
5. Only return matches with confidence >= 0.7
6. If the speaker doesn't identify their role/relationship, return empty array
7. Rely on what the customer says or confirms vs what the AI assistant suggests

Confidence Guidelines:
- 0.9-1.0: Explicit statement ("I am the owner", "I'm a renter")
- 0.8-0.9: Strong implication ("my rental unit", "the property I own", "I'm the landlord")
- 0.7-0.8: Moderate implication ("I live here", "I work for this building", "I'm interested in renting")
- Below 0.7: Don't include

JSON Response Format:
{
  "groupMatches": [{
    "groupType": "OWNER" | "TENANT" | "VENDOR" | null,
    "customGroup": "string (only if groupType is null)",
    "confidence": number,
    "matchDetails": "explanation string"
  }]
}

Important:
- Use groupType for standard types (OWNER, TENANT, VENDOR)
- Use customGroup for non-standard types (board member, staff, contractor)
- Never set both groupType AND customGroup for the same match
- Sort matches by confidence descending`,
    variables: ['content'],
    variableSchema: z.object({
        content: z.string().describe('Transcript / Message'),
    }),
    responseSchema: GroupDetectionResponseSchema,
    evalFields: [
        {
            key: 'groupType',
            type: 'text',
            extract: (r) => r.groupMatches[0]?.groupType ?? null,
        },
        {
            key: 'customGroup',
            type: 'text',
            extract: (r) => r.groupMatches[0]?.customGroup ?? null,
        },
        {
            key: 'confidence',
            type: 'number',
            extract: (r) => r.groupMatches[0]?.confidence ?? null,
        },
        {
            key: 'allGroupTypes',
            type: 'text[]',
            extract: (r) => r.groupMatches
                .map(m => m.groupType)
                .filter((t) => t != null),
        },
    ],
    metadata: {
        model: 'gpt-4o-mini',
        temperature: 0.2,
        maxTokens: 1000,
        responseFormat: 'json_object',
    },
};
//# sourceMappingURL=contact-group-matching.js.map